import { useState, useEffect, useCallback } from 'react';
import fetchAPI from '../helper/axios';
import fetchAPICustom from '../helper/custom';

// Types
export interface GroOneAppData {
  hero: {
    data: any;
    images: {
      background: any;
      mobile: any;
      truck: any;
    };
  };
  about: {
    data: any;
    image: any;
  };
  stats: {
    data: any;
    images: {
      googlePlay: any;
      appStore: any;
    };
  };
  sliders: {
    mainWeb: any;
    mainMobile: any;
    kavach: any;
    fastTag: any;
    dhan: any;
    fleet: any;
  };
  faq: any;
  footer: any;
}

// Cache storage
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchWithCache = async (endpoint: string, isCustom = false): Promise<any> => {
  const now = Date.now();
  const cached = cache.get(endpoint);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const data = isCustom ? await fetchAPICustom(endpoint) : await fetchAPI(endpoint);
  cache.set(endpoint, { data, timestamp: now });
  return data;
};

// API endpoints configuration
const API_ENDPOINTS = {
  hero: [
    { endpoint: 'node/26', isCustom: false },
    { endpoint: 'media/41/edit', isCustom: false }, // mobile image
    { endpoint: 'media/42/edit', isCustom: false }, // background image
    { endpoint: 'media/43/edit', isCustom: false }, // truck image
  ],
  about: [
    { endpoint: 'node/27', isCustom: false },
    { endpoint: 'media/34/edit', isCustom: false },
  ],
  stats: [
    { endpoint: 'node/28', isCustom: false },
    { endpoint: 'media/35/edit', isCustom: false }, // google play
    { endpoint: 'media/36/edit', isCustom: false }, // app store
  ],
  //  sliders:
  //  // [
  // //   { endpoint: 'webslider.php?id=12', isCustom: true }, // main web
  // //   { endpoint: 'webslider.php?id=8', isCustom: true },  // main mobile
  // //   { endpoint: 'webslider.php?id=7', isCustom: true },  // kavach
  // //   { endpoint: 'webslider.php?id=9', isCustom: true },  // fasttag
  // //   { endpoint: 'webslider.php?id=10', isCustom: true }, // dhan
  // //   { endpoint: 'webslider.php?id=11', isCustom: true }, // fleet
  //  ],
  faq: { endpoint: 'faq.php', isCustom: true },
//  footer: { endpoint: 'node/31', isCustom: false },
};

export const useGroOneAppData = () => {
  const [data, setData] = useState<GroOneAppData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0); // for retry trigger
  const [loadingProgress, setLoadingProgress] = useState<{
    hero: boolean;
    about: boolean;
    stats: boolean;
     sliders: boolean;
    faq: boolean;
     footer: boolean;
  }>({
    hero: true,
    about: false,
    stats: false,
     sliders: false,
    faq: false,
     footer: false,
  });

  const loadSection = useCallback(async (endpoints: Array<{endpoint: string, isCustom: boolean}>, section: keyof typeof loadingProgress) => {
    try {
      const results = await Promise.all(
        endpoints.map(({ endpoint, isCustom }) => fetchWithCache(endpoint, isCustom))
      );
      return results;
    } catch (err) {
      console.error(`Error loading ${section} section:`, err);
      throw err;
    }
  }, []);

  const updateData = useCallback((partialData: Partial<GroOneAppData>) => {
    setData(prev => prev ? { ...prev, ...partialData } : partialData as GroOneAppData);
  }, []);

  // Process slider data
  const processSliderData = (sliderData: any) => {
    if (!sliderData?.data?.slider_images) return sliderData;
    
    const processedImages = sliderData.data.slider_images.map((slide: any) => ({
      ...slide,
      slider_img_url: slide.slider_img_url?.replace("public://", "") || ''
    }));
    
    return {
      ...sliderData,
      data: {
        ...sliderData.data,
        slider_images: processedImages
      }
    };
  };

  // Process footer URLs
  // const processFooterUrls = (footerData: any) => {
  //   let googlePlayUrl = '';
  //   let appStoreUrl = '';
    
  //   if (footerData?.field_link_url) {
  //     for (const url of footerData.field_link_url) {
  //       if (url.title === 'Google Play') {
  //         googlePlayUrl = url.uri.replace("internal:", "");
  //       }
  //       if (url.title === 'App Store') {
  //         appStoreUrl = url.uri.replace("internal:", "");
  //       }
  //     }
  //   }
    
  //   return { googlePlayUrl, appStoreUrl };
  // };

  useEffect(() => {
    const loadDataProgressive = async () => {
      try {
       // console.log('Starting GroOneApp data load...');
        
        // Load hero data first
       // console.log('Loading hero data...');
        const heroResults = await loadSection(API_ENDPOINTS.hero, 'hero');
        
        updateData({
          hero: {
            data: heroResults[0],
            images: {
              mobile: heroResults[1],
              background: heroResults[2],
              truck: heroResults[3],
            }
          }
        });
        setLoadingProgress(prev => ({ ...prev, hero: false, about: true }));

        // Load about data
        console.log('Loading about data...');
        const aboutResults = await loadSection(API_ENDPOINTS.about, 'about');
        
        updateData({
          about: {
            data: aboutResults[0],
            image: aboutResults[1]
          }
        });
        setLoadingProgress(prev => ({ ...prev, about: false, stats: true }));

        // Load stats data
        console.log('Loading stats data...');
        const statsResults = await loadSection(API_ENDPOINTS.stats, 'stats');
        
        updateData({
          stats: {
            data: statsResults[0],
            images: {
              googlePlay: statsResults[1],
              appStore: statsResults[2],
            }
          }
        });
        setLoadingProgress(prev => ({ ...prev, stats: false, sliders: false }));

        // Load sliders data
        // console.log('Loading sliders data...');
        // const sliderResults = await loadSection(API_ENDPOINTS.sliders, 'sliders');
        
        // updateData({
        //   sliders: {
        //     mainWeb: processSliderData(sliderResults[0]),
        //     mainMobile: processSliderData(sliderResults[1]),
        //     kavach: processSliderData(sliderResults[2]),
        //     fastTag: processSliderData(sliderResults[3]),
        //     dhan: processSliderData(sliderResults[4]),
        //     fleet: processSliderData(sliderResults[5]),
        //   }
        // });
        setLoadingProgress(prev => ({ ...prev, sliders: false, faq: true }));

        // Load FAQ data
        console.log('Loading FAQ data...');
        const faqResult = await fetchWithCache(API_ENDPOINTS.faq.endpoint, API_ENDPOINTS.faq.isCustom);
        
        updateData({
          faq: faqResult
        });
        setLoadingProgress(prev => ({ ...prev, faq: false, footer: false }));

        // Load footer data
        // console.log('Loading footer data...');
        // const footerResult = await fetchWithCache(API_ENDPOINTS.footer.endpoint, API_ENDPOINTS.footer.isCustom);
        
        // updateData({
        //   footer: footerResult
        // });
        // setLoadingProgress(prev => ({ ...prev, footer: false }));

        console.log('GroOneApp data loaded successfully');
        setLoading(false);
      } catch (err: any) {
        console.error('Error loading GroOneApp data:', err);
        setError(err.message || 'Failed to load GroOneApp data');
          // Retry after 3 seconds
        setTimeout(() => setRetry((prev) => prev + 1), 10000);
        setLoading(false);
      }
    };

    loadDataProgressive();
  }, [loadSection, updateData,retry]);

  return { 
    data, 
    loading, 
    error, 
    loadingProgress,
    retry: () => {
      setLoading(true);
      setError(null);
      setLoadingProgress({
        hero: true,
        about: false,
        stats: false,
        sliders: false,
        faq: false,
        footer: false,
      });
    }
  };
};
